import { Zone } from '../types';

export interface KMLValidationResult {
  isValid: boolean;
  error?: string;
  zones?: Omit<Zone, 'id'>[];
}

export class KMLParser {
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly ALLOWED_MIME_TYPES = [
    'application/vnd.google-earth.kml+xml',
    'application/xml',
    'text/xml',
    'application/octet-stream' // Parfois les fichiers .kml sont détectés comme octet-stream
  ];

  /**
   * Valide et parse un fichier KML de manière sécurisée
   */
  static async parseKMLFile(file: File): Promise<KMLValidationResult> {
    try {
      // Validation 1: Vérifier l'extension
      if (!file.name.toLowerCase().endsWith('.kml')) {
        return {
          isValid: false,
          error: 'Le fichier doit avoir l\'extension .kml'
        };
      }

      // Validation 2: Vérifier la taille
      if (file.size > this.MAX_FILE_SIZE) {
        return {
          isValid: false,
          error: 'Le fichier est trop volumineux (maximum 5MB)'
        };
      }

      // Validation 3: Vérifier le type MIME
      if (!this.ALLOWED_MIME_TYPES.includes(file.type) && file.type !== '') {
        return {
          isValid: false,
          error: 'Type de fichier non autorisé'
        };
      }

      // Lire le contenu du fichier
      const content = await this.readFileAsText(file);

      // Validation 4: Vérifier que c'est du XML valide
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(content, 'text/xml');
      
      // Vérifier s'il y a des erreurs de parsing XML
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        return {
          isValid: false,
          error: 'Le fichier n\'est pas un XML valide'
        };
      }

      // Validation 5: Vérifier que c'est un fichier KML valide
      const kmlElement = xmlDoc.querySelector('kml');
      if (!kmlElement) {
        return {
          isValid: false,
          error: 'Le fichier ne contient pas d\'élément KML valide'
        };
      }

      // Validation 6: Vérifier le namespace KML
      const kmlNamespace = kmlElement.getAttribute('xmlns');
      if (!kmlNamespace || !kmlNamespace.includes('opengis.net/kml')) {
        return {
          isValid: false,
          error: 'Namespace KML invalide'
        };
      }

      // Parser les zones
      const zones = await this.parseZonesFromKML(xmlDoc);

      return {
        isValid: true,
        zones
      };

    } catch (error) {
      console.error('Erreur lors du parsing KML:', error);
      return {
        isValid: false,
        error: 'Erreur lors de l\'analyse du fichier'
      };
    }
  }

  /**
   * Lit un fichier comme texte
   */
  private static readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          resolve(e.target.result as string);
        } else {
          reject(new Error('Impossible de lire le fichier'));
        }
      };
      reader.onerror = () => reject(new Error('Erreur de lecture du fichier'));
      reader.readAsText(file);
    });
  }

  /**
   * Parse les zones depuis un document KML
   */
  private static async parseZonesFromKML(xmlDoc: Document): Promise<Omit<Zone, 'id'>[]> {
    const zones: Omit<Zone, 'id'>[] = [];

    // Chercher tous les Placemark qui contiennent des Polygon
    const placemarks = xmlDoc.querySelectorAll('Placemark');

    for (const placemark of placemarks) {
      const polygon = placemark.querySelector('Polygon');
      if (!polygon) continue;

      // Extraire le nom
      const nameElement = placemark.querySelector('name');
      const name = nameElement?.textContent?.trim() || `Zone_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;


      // Extraire les coordonnées du contour extérieur
      const outerBoundary = polygon.querySelector('outerBoundaryIs LinearRing coordinates');
      if (!outerBoundary) continue;

      const coordinatesText = outerBoundary.textContent?.trim();
      if (!coordinatesText) continue;

      try {
        const coordinates = this.parseCoordinates(coordinatesText);
        if (coordinates.length < 3) continue; // Un polygone doit avoir au moins 3 points

        // Validation du nom - ne doit pas être vide
        if (!name || name.trim() === '') continue;

        const zone: Omit<Zone, 'id'> = {
          name: name.trim(),
          coordinates
        };

        zones.push(zone);

      } catch (error) {
        console.warn(`Impossible de parser les coordonnées pour ${name}:`, error);
        continue;
      }
    }

    if (zones.length === 0) {
      throw new Error('Aucune zone valide trouvée dans le fichier KML');
    }

    return zones;
  }

  /**
   * Parse les coordonnées depuis le texte KML
   */
  private static parseCoordinates(coordinatesText: string): Array<{latitude: number; longitude: number}> {
    const coords: Array<{latitude: number; longitude: number}> = [];
    
    // Les coordonnées KML sont au format: longitude,latitude,altitude (altitude optionnelle)
    // Séparées par des espaces ou des retours à la ligne
    const points = coordinatesText.split(/\s+/).filter(point => point.trim());

    for (const point of points) {
      const parts = point.split(',');
      if (parts.length < 2) continue;

      const longitude = parseFloat(parts[0]);
      const latitude = parseFloat(parts[1]);

      // Validation des coordonnées
      if (isNaN(longitude) || isNaN(latitude)) continue;
      if (latitude < -90 || latitude > 90) continue;
      if (longitude < -180 || longitude > 180) continue;

      coords.push({ latitude, longitude });
    }

    return coords;
  }

}